import { api } from "@/shared/api";

/**
 * 报价口 —— **全站唯一一处「多少钱」的出处**(批 L)。
 *
 * <p>在这之前,同一笔出图的价钱有三个出处:卡墙的 `creditRates.ts` 常量、
 * 创建页底栏读的 `provider-capabilities.tierCredits`、以及结算时后端真算的那一个。
 * 三处对得上纯属巧合 —— 出图线之所以一直没出事,是因为它的新旧加价率都是 ×10;
 * 换成 ×1.6 的那几条线全都对不上了。
 */

/** 后端 `PriceQuote2Dto`。dryRun 时 `quoteId` / `expiresAt` 为 null —— **没有单号就是没有单**。 */
export interface PriceQuote {
  quoteId: string | null;
  skuCode: string;
  variantKey: string | null;
  qty: number;
  credits: number;
  breakdownJson?: string | null;
  expiresAt: string | null;
}

export interface QuoteParams {
  skuCode: string;
  variantKey?: string | null;
  qty?: number;
  /** true = 只算不落库(底栏实时预估);false/省略 = 落单并返回单号(确认卡)。 */
  dryRun?: boolean;
}

/** Backend PricingCatalogService.CatalogRow. creditsPerUnit = credits when qty is 1. */
export interface PriceCatalogRow {
  skuCode: string;
  displayName: string | null;
  capability: string | null;
  layer: string;
  pricingMode: string;
  unit: string | null;
  variantKey: string | null;
  modelCode: string | null;
  /** qty=1 的积分,**向上取整**(对不足 1 分的线是上界:省流长口播 3.5 → 4)。 */
  creditsPerUnit: number;
  /**
   * 同一单价**不取整**的读数(如 3.5;0927 K4 I 起后端下发)。卡面标「约 N 积分 / 秒」这类**单价**用它。
   * 可选:老后端没有这一列 → undefined,`PriceBook.unitExact()` 返 null(不兜整数上界)。
   */
  creditsPerUnitExact?: number | null;
}

export const pricingService = {
  /**
   * 价目表(卡墙用)。整表一次取回,前端缓存 —— 卡墙不做逐卡询价(那会是几十个请求)。
   *
   * 服务端每一行都真去调 priceOf,不是照公式再算一遍;
   * 判据钉的是「catalog 的每个数 == 同参数 quote 的数」(PricingCatalogMatchesQuoteIT)。
   */
  catalog: () => api.get<PriceCatalogRow[]>("/pricing/catalog"),

  /**
   * 报价。
   *
   * ★ `dryRun: true` 只算不落库 —— 用户在编辑期可能改十几次参数,每次落一行是灾难,
   *   而且报价单 15 分钟有效期覆盖不了「慢慢调」的人。
   * ★ `dryRun: false` 落单并返回单号 —— **确认卡上显示的数必须来自这张单**,
   *   否则等于没解决问题:用户看的是 A、扣的是 B,而 A 是前端自己算的。
   */
  quote: (p: QuoteParams) =>
    api.post<PriceQuote>("/pricing/quote", {
      skuCode: p.skuCode,
      variantKey: p.variantKey ?? null,
      qty: p.qty ?? 1,
      dryRun: p.dryRun ?? false,
    }),
};

/** 出图 / 长图的规格档 key:`{provider小写}_{档位}`(与后端 `xb_price_sku_variant.variant_key` 逐字一致)。 */
export function imageVariantKey(provider: string, tier: string): string {
  return `${provider.trim().toLowerCase()}_${tier.trim().toUpperCase()}`;
}

export const SKU = {
  posterImage: "poster.image",
  longImage: "longimage.render",
  tryon: "tryon.image",
  video: "video.render",
  dhRender: "dh.render",
} as const;
